import { FormInputBlock } from './form-input-block.js'
import { assignCommonProperties } from '../../util/data/assign-common-properties.js'
import { BoReference } from '../../bos/bo-reference.js'
import { Block } from '../block.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
	jsonMapProperty,
} from '@sumbricht/json-class-serializer'
import { BoVisitor } from '../../bos/bo-visitor.js'
import { FormControlElement, FormElement } from './form-elements.js'
import { ScreenEditorInput } from '../decorators/screen-editor-input.js'

@jsonClass('FormBlock')
export class FormBlock extends Block {
	@jsonProperty()
	@ScreenEditorInput({
		inputType: 'input',
		order: 1,
		label: 'Form variable name',
		contentType: 'text',
		elementType: 'input',
	})
	formVariable: string = 'form'

	@jsonProperty()
	@ScreenEditorInput({
		inputType: 'checkbox',
		order: 2,
		label: 'Prevent navigation/closing if dirty',
		tooltip: () => ({
			text: 'If enabled, the user will be prevented from navigating away from the screen if the form is dirty (has unsaved changes).',
		}),
		isBinding: false,
	})
	preventNavigationIfDirty: boolean = true

	@jsonMapProperty(String, BoReference)
	variables: Map<string, BoReference> = new Map()

	constructor(init: Partial<FormBlock> = {}) {
		super()
		this.init(init)
	}

	getFormElements() {
		const els: FormElement[] = []

		this.visit(
			(block, parent) => {
				if (block instanceof FormInputBlock) {
					const [variable, bindingPropertyName] =
						block.bindingCode?.content?.split('.') ?? []
					const boRef = this.variables.get(variable)

					els.push(
						new FormControlElement({
							variable,
							boRef,
							bindingPropertyName,
							block,
							parentBlock: parent ?? undefined,
						}),
					)
				}
			},
			null,
			0,
			'first',
		)

		return els
	}

	getVariableScope(
		includeDeclarationVariables: boolean,
	): Record<string, string> {
		const superScope = super.getVariableScope(includeDeclarationVariables)

		return this.formVariable
			? {
					...superScope,
					[this.formVariable]: 'Sys.Types.Form',
				}
			: superScope
	}

	visitThisWithBoVisitor(
		visitor: BoVisitor,
		pathPrefix: (string | number)[],
	): void {
		for (const [variable, boRef] of this.variables.entries()) {
			visitor.visitBoReference(boRef, ['variables', variable])
		}
	}

	getEditorCategory(): string {
		return 'Additional Form Fields'
	}
}
